import { Button } from "../../components/Button";
import { PaymentBusy, type PaymentStep } from "../../components/PaymentBusy";
import { ALL_ACCESS_MINOR, naira } from "../../lib/format";

interface UpgradeWallScreenProps {
  /** Scores stay visible — the wall is on practice, not on knowing where you are. */
  subjects: { subject: string; readiness: number; weakTopic: string }[];
  onPay?: (() => void) | undefined;
  onAskParent?: (() => void) | undefined;
  /**
   * Close the wall.
   *
   * Screen 13 specifies a dismiss and there was none, so the wall was a room
   * with no door: a student who was not ready to pay had no way out of it. It
   * goes Home rather than back, because back is the question they were
   * blocked on and would return them straight here.
   */
  onDismiss?: (() => void) | undefined;
  /** Opening Paystack, or confirming a charge it reported. */
  step?: PaymentStep | undefined;
}

const INCLUDED = [
  "Unlimited past questions & topic practice",
  "AI tutor that explains till you get it",
  "Weekly mock CBTs, real timing",
  "Works offline, syncs when you're back",
];

/**
 * Screen 13 — the sanctioned two-primary exception.
 *
 * Every other screen has exactly one ember-solid action. Here "Ask a parent to
 * pay" carries equal visual weight in info-blue, because for an under-18 the
 * share path *is* the conversion path: they cannot pay, so demoting it to a
 * text link would be styling the only route they have as an afterthought.
 */
export function UpgradeWallScreen({
  subjects,
  onPay,
  onAskParent,
  onDismiss,
  step = null,
}: UpgradeWallScreenProps) {
  return (
    <div className="app-column px-5 pb-6 pt-6">
      {onDismiss ? (
        <button
          type="button"
          aria-label="Close"
          onClick={onDismiss}
          className="-m-3 mb-0 flex h-11 w-11 items-center justify-center self-start p-3 text-[18px] leading-none text-text-muted"
        >
          ✕
        </button>
      ) : null}

      <ul className="m-0 flex list-none gap-2 p-0">
        {subjects.map((entry) => (
          <li
            key={entry.subject}
            className="flex flex-1 flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-surface p-3"
          >
            <span className="text-[12px] text-text-muted">{entry.subject}</span>
            <span className="tabular font-[family-name:var(--font-display)] text-[22px] font-bold leading-none">
              {entry.readiness}%
            </span>
            <span className="text-[11px] text-negative">{entry.weakTopic} weak</span>
          </li>
        ))}
      </ul>

      <h1 className="m-0 pt-6 font-[family-name:var(--font-display)] text-[26px] font-bold leading-[1.15]">
        You've seen your score. Now raise it.
      </h1>

      <div className="flex items-baseline gap-2 pt-5">
        <span className="tabular font-[family-name:var(--font-display)] text-[38px] font-bold leading-none">
          {naira(ALL_ACCESS_MINOR)}
        </span>
      </div>
      <p className="m-0 pt-1 text-[13px] text-text-muted">
        one payment, no renewals — till your exam
      </p>

      <ul className="m-0 flex list-none flex-col gap-2.5 p-0 pt-6">
        {INCLUDED.map((item) => (
          <li key={item} className="flex items-start gap-2.5 text-[13.5px] leading-[1.45]">
            <span aria-hidden="true" className="pt-0.5 text-positive">
              ✓
            </span>
            {item}
          </li>
        ))}
      </ul>

      <div className="mt-auto flex flex-col gap-3 pt-8">
        <Button disabled={step !== null} onClick={onPay}>
          {step === "opening" ? "Opening secure checkout…" : `Pay ${naira(ALL_ACCESS_MINOR)} now`}
        </Button>
        <Button variant="info" onClick={onAskParent}>
          Ask a parent to pay
        </Button>
        <p className="m-0 text-center text-[12.5px] text-text-muted">
          Your score and streak stay safe either way.
        </p>
      </div>
      <PaymentBusy step={step} />
    </div>
  );
}
